import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

// Function to calculate the Market Potential Score
const calculateNicheMarketPotentialScore = (keywords) => {
    if (!keywords.length) return 0; // Handle empty data

    const volumes = keywords.map(k => parseFloat(k.volume.replace(/,/g, '')) || 0);
    const cpcs = keywords.map(k => parseFloat(k.cpc) || 0);
    const difficulties = keywords.map(k => parseFloat(k.difficulty) || 0);

    const minVolume = Math.min(...volumes);
    const maxVolume = Math.max(...volumes);
    const minCPC = Math.min(...cpcs);
    const maxCPC = Math.max(...cpcs);
    const minDifficulty = Math.min(...difficulties);
    const maxDifficulty = Math.max(...difficulties);

    function normalize(value, min, max) {
        if (max === min) return 0;
        return (value - min) / (max - min);
    }

    const volumeWeight = 0.50;
    const cpcWeight = 0.30;
    const difficultyWeight = 0.20;

    const scores = keywords.map(keyword => {
        const normalizedVolume = normalize(parseFloat(keyword.volume.replace(/,/g, '')), minVolume, maxVolume);
        const normalizedCPC = 1 - normalize(parseFloat(keyword.cpc), minCPC, maxCPC);
        const normalizedDifficulty = 1 - normalize(parseFloat(keyword.difficulty), minDifficulty, maxDifficulty);

        const weightedVolume = normalizedVolume * volumeWeight;
        const weightedCPC = normalizedCPC * cpcWeight;
        const weightedDifficulty = normalizedDifficulty * difficultyWeight;

        return weightedVolume + weightedCPC + weightedDifficulty;
    });

    const nicheMarketPotentialScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;

    return nicheMarketPotentialScore * 100;
};

const NicheMarketPotentialScore = ({ data }) => {
    const svgRef = useRef(null);

    useEffect(() => {
        const marketPotential = calculateNicheMarketPotentialScore(data);

        const width = 110;
        const height = 110;
        const radius = Math.min(width, height) / 2;
        const strokeWidth = 20;
        const backgroundColor = '#e6e6e6';

        const getColorForPotential = (score) => {
            if (score >= 0 && score <= 25) return '#f44336'; 
            if (score >= 26 && score <= 50) return '#ff9800'; 
            if (score >= 51 && score <= 75) return '#4caf50'; 
            if (score >= 76 && score <= 100) return '#90ee90'; 
            return '#e6e6e6'; // Default color
        };

        // const clampedPotential = Math.max(0, Math.min(marketPotential, 100));
        const color = getColorForPotential(marketPotential.toFixed(0));

        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', height)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .append('g')
            .attr('transform', `translate(${width / 2}, ${height / 2})`);

        const arc = d3.arc()
            .innerRadius(radius - strokeWidth)
            .outerRadius(radius)
            .startAngle(0);

        svg.append('path')
            .attr('d', arc.endAngle(2 * Math.PI))
            .attr('fill', backgroundColor);

        svg.append('path')
            .attr('d', arc.endAngle((2 * Math.PI) * (marketPotential / 100)))
            .attr('fill', color);

        svg.append('text')
            .attr('y', 0)
            .attr('x', 0)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', '18px')
            .text(`${marketPotential.toFixed(2)}%`)
            .style('font-family', 'Quicksand');

        return () => {
            d3.select(svgRef.current).selectAll('*').remove();
        };
    }, [data]);

    return <svg ref={svgRef} />;
};

export default NicheMarketPotentialScore;
